import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  DollarSign,
  Plus,
  Edit2,
  Trash2,
  X,
  CheckCircle,
  Clock,
  Send,
  RefreshCw,
  Users,
  Filter
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PROGRAM_OPTIONS = [
  'SBA Loan',
  'Term Loan',
  'Line of Credit',
  'Equipment Financing',
  'Merchant Processing',
  'Jumbo Term Loan',
  'Term Loan (3-5yr)',
  'Term Loan (10yr)',
  'Term Loan / LOC Hybrid',
  'Transportation Factoring',
  'Real Estate Financing',
  'Other'
];

const STATUS_OPTIONS = [
  { value: 'unpaid', label: 'Unpaid', color: 'bg-amber-100 text-amber-800' },
  { value: 'paid', label: 'Paid', color: 'bg-blue-100 text-blue-800' },
  { value: 'confirmed', label: 'Confirmed', color: 'bg-emerald-100 text-emerald-800' },
];

const getStatusBadge = (status) => {
  const opt = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
  return <Badge className={`${opt.color} text-xs`} data-testid={`commission-status-${status}`}>{opt.label}</Badge>;
};

const AdminCommissions = () => {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brokers, setBrokers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [brokerFilter, setBrokerFilter] = useState('all');
  const [formData, setFormData] = useState({
    borrower_company_name: '',
    date_funded: '',
    program: '',
    loan_loc_draw: '',
    amount_funded: '',
    commission_amount: '',
    status: 'unpaid',
    visible_to: [],
    notify_brokers: false
  });

  const fetchCommissions = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/commissions`);
      setCommissions(res.data.commissions || []);
    } catch (err) {
      console.error('Error fetching commissions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBrokers = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/brokers`);
      setBrokers(res.data || []);
    } catch (err) {
      console.error('Error fetching brokers:', err);
    }
  }, []);

  useEffect(() => {
    fetchCommissions();
    fetchBrokers();
  }, [fetchCommissions, fetchBrokers]);

  const resetForm = () => {
    setFormData({
      borrower_company_name: '',
      date_funded: '',
      program: '',
      loan_loc_draw: '',
      amount_funded: '',
      commission_amount: '',
      status: 'unpaid',
      visible_to: [],
      notify_brokers: false
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (comm) => {
    setFormData({
      borrower_company_name: comm.borrower_company_name || '',
      date_funded: comm.date_funded || '',
      program: comm.program || '',
      loan_loc_draw: comm.loan_loc_draw || '',
      amount_funded: comm.amount_funded || '',
      commission_amount: comm.commission_amount || '',
      status: comm.status || 'unpaid',
      visible_to: comm.visible_to || [],
      notify_brokers: false
    });
    setEditingId(comm.id);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!formData.borrower_company_name.trim()) {
      alert('Borrower Company Name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        amount_funded: formData.amount_funded ? parseFloat(String(formData.amount_funded).replace(/[$,]/g, '')) : null,
        commission_amount: formData.commission_amount ? parseFloat(String(formData.commission_amount).replace(/[$,]/g, '')) : null,
      };

      if (editingId) {
        await axios.put(`${BACKEND_URL}/api/commissions/${editingId}`, payload);
      } else {
        await axios.post(`${BACKEND_URL}/api/commissions`, payload);
      }
      resetForm();
      fetchCommissions();
    } catch (err) {
      console.error('Error saving commission:', err);
      alert(err.response?.data?.detail || 'Failed to save commission');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this commission record?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/commissions/${id}`);
      fetchCommissions();
    } catch (err) {
      console.error('Error deleting commission:', err);
    }
  };

  const toggleBroker = (brokerId) => {
    setFormData(prev => ({
      ...prev,
      visible_to: prev.visible_to.includes(brokerId)
        ? prev.visible_to.filter(id => id !== brokerId)
        : [...prev.visible_to, brokerId]
    }));
  };

  const fmt = (val) => {
    if (val == null || val === '') return '—';
    return `$${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const fmtDate = (val) => {
    if (!val) return '—';
    try { return new Date(val).toLocaleDateString(); } catch { return val; }
  };

  // Build broker options for filter
  const brokerFilterOptions = React.useMemo(() => {
    const seen = new Map();
    commissions.forEach(c => {
      (c.visible_to || []).forEach((id, i) => {
        const name = (c.visible_to_names || [])[i] || 'Unknown';
        if (!seen.has(id)) seen.set(id, name);
      });
    });
    return [{ value: 'all', label: 'All Brokers' }, ...[...seen.entries()].map(([id, name]) => ({ value: id, label: name }))];
  }, [commissions]);

  const filteredCommissions = React.useMemo(() => {
    if (brokerFilter === 'all') return commissions;
    return commissions.filter(c => (c.visible_to || []).includes(brokerFilter));
  }, [commissions, brokerFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-800" data-testid="admin-commissions-title">Commission Records</h3>
          <p className="text-sm text-slate-500">
            {brokerFilter !== 'all' ? `${filteredCommissions.length} of ${commissions.length}` : commissions.length} total records
          </p>
        </div>
        <div className="flex items-center gap-2">
          {brokerFilterOptions.length > 2 && (
            <div className="relative">
              <Filter className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
              <select
                value={brokerFilter}
                onChange={(e) => setBrokerFilter(e.target.value)}
                className="pl-8 pr-8 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500 appearance-none cursor-pointer min-w-[160px]"
                data-testid="commission-broker-filter"
              >
                {brokerFilterOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {brokerFilter !== 'all' && (
                <button onClick={() => setBrokerFilter('all')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}
          <Button variant="outline" size="sm" onClick={fetchCommissions} data-testid="refresh-commissions">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button size="sm" className="bg-teal-600 hover:bg-teal-700" onClick={() => { resetForm(); setShowForm(true); }} data-testid="new-commission-btn">
            <Plus className="h-4 w-4 mr-1" /> New Commission
          </Button>
        </div>
      </div>

      {/* Create / Edit Form */}
      {showForm && (
        <Card className="border-teal-200 shadow-md" data-testid="commission-form">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center justify-between">
              <span>{editingId ? 'Edit Commission' : 'New Commission'}</span>
              <button onClick={resetForm} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label>Borrower Company Name *</Label>
                <Input
                  value={formData.borrower_company_name}
                  onChange={(e) => setFormData(p => ({ ...p, borrower_company_name: e.target.value }))}
                  placeholder="Company name"
                  data-testid="commission-borrower-name"
                />
              </div>
              <div>
                <Label>Date Funded</Label>
                <Input
                  type="date"
                  value={formData.date_funded}
                  onChange={(e) => setFormData(p => ({ ...p, date_funded: e.target.value }))}
                  data-testid="commission-date-funded"
                />
              </div>
              <div>
                <Label>Program</Label>
                <select
                  value={formData.program}
                  onChange={(e) => setFormData(p => ({ ...p, program: e.target.value }))}
                  className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  data-testid="commission-program"
                >
                  <option value="">Select program</option>
                  {PROGRAM_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <Label>Loan / LOC Draw</Label>
                <Input
                  value={formData.loan_loc_draw}
                  onChange={(e) => setFormData(p => ({ ...p, loan_loc_draw: e.target.value }))}
                  placeholder="e.g. Initial Draw"
                  data-testid="commission-draw"
                />
              </div>
              <div>
                <Label>Amount Funded</Label>
                <Input
                  value={formData.amount_funded}
                  onChange={(e) => setFormData(p => ({ ...p, amount_funded: e.target.value }))}
                  placeholder="$0.00"
                  data-testid="commission-amount-funded"
                />
              </div>
              <div>
                <Label>Commission Amount</Label>
                <Input
                  value={formData.commission_amount}
                  onChange={(e) => setFormData(p => ({ ...p, commission_amount: e.target.value }))}
                  placeholder="$0.00"
                  data-testid="commission-amount"
                />
              </div>
              <div>
                <Label>Status</Label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData(p => ({ ...p, status: e.target.value }))}
                  className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  data-testid="commission-status-select"
                >
                  {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
            </div>

            {/* Assign Brokers */}
            <div>
              <Label className="flex items-center gap-1.5 mb-2">
                <Users className="h-4 w-4" /> Assign to Brokers
              </Label>
              {brokers.length === 0 ? (
                <p className="text-sm text-slate-400">No brokers found.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                  {brokers.map(b => (
                    <div
                      key={b.id}
                      className={`flex items-center gap-2 p-2.5 border rounded-lg cursor-pointer text-sm transition-colors ${
                        formData.visible_to.includes(b.id)
                          ? 'bg-teal-50 border-teal-400'
                          : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => toggleBroker(b.id)}
                      data-testid={`assign-broker-${b.id}`}
                    >
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        formData.visible_to.includes(b.id) ? 'bg-teal-600 border-teal-600' : 'border-slate-300'
                      }`}>
                        {formData.visible_to.includes(b.id) && (
                          <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <div className="truncate">
                        <span className="font-medium">{b.name}</span>
                        {b.company_name && <span className="text-slate-400 ml-1">({b.company_name})</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {formData.visible_to.length > 0 && (
                <p className="text-xs text-teal-600 mt-1">{formData.visible_to.length} broker{formData.visible_to.length > 1 ? 's' : ''} selected</p>
              )}
            </div>

            {/* Notify checkbox */}
            {!editingId && formData.visible_to.length > 0 && (
              <div
                className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                  formData.notify_brokers ? 'bg-blue-50 border-blue-300' : 'border-slate-200 hover:border-slate-300'
                }`}
                onClick={() => setFormData(p => ({ ...p, notify_brokers: !p.notify_brokers }))}
                data-testid="commission-notify-toggle"
              >
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                  formData.notify_brokers ? 'bg-blue-600 border-blue-600' : 'border-slate-300'
                }`}>
                  {formData.notify_brokers && (
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700">Send email notification to assigned brokers</p>
                  <p className="text-xs text-slate-400">Brokers will receive an email with the commission details</p>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={resetForm} disabled={saving}>Cancel</Button>
              <Button className="bg-teal-600 hover:bg-teal-700" onClick={handleSubmit} disabled={saving} data-testid="commission-save-btn">
                {saving ? 'Saving...' : editingId ? 'Update' : 'Create Commission'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Commissions Table */}
      {filteredCommissions.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <DollarSign className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 mb-1">{commissions.length === 0 ? 'No commission records yet' : 'No commissions match the selected filter'}</p>
            <p className="text-sm text-slate-400">{commissions.length === 0 ? 'Click "New Commission" to create one.' : 'Try changing the broker filter.'}</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="commissions-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Borrower</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Date Funded</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Date Created</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Program</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Loan/LOC Draw</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Amount Funded</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Commission</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Assigned To</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCommissions.map((comm) => (
                    <tr key={comm.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`commission-row-${comm.id}`}>
                      <td className="px-4 py-3 font-medium text-slate-800">{comm.borrower_company_name}</td>
                      <td className="px-4 py-3 text-slate-600">{comm.date_funded || '—'}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{fmtDate(comm.created_at)}</td>
                      <td className="px-4 py-3 text-slate-600">{comm.program || '—'}</td>
                      <td className="px-4 py-3 text-slate-600">{comm.loan_loc_draw || '—'}</td>
                      <td className="px-4 py-3 text-right text-slate-700 font-medium">{fmt(comm.amount_funded)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-emerald-700">{fmt(comm.commission_amount)}</td>
                      <td className="px-4 py-3">{getStatusBadge(comm.status)}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(comm.visible_to_names || []).length > 0
                            ? comm.visible_to_names.map((name, i) => (
                                <Badge key={i} variant="outline" className="text-xs">{name}</Badge>
                              ))
                            : <span className="text-slate-400 text-xs">None</span>
                          }
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="outline" size="sm" onClick={() => handleEdit(comm)} data-testid={`edit-commission-${comm.id}`}>
                            <Edit2 className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleDelete(comm.id)} className="text-red-600 hover:text-red-700 hover:border-red-300" data-testid={`delete-commission-${comm.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminCommissions;
